import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ScreenSize, useGetScreenSize } from '../../hooks/useGetScreenSize';
import { buildCategoryPath, parseCategoryFromUrl } from '../../utils/url';
import { feedActions, useActions, useFeedSelector } from '../../store';

import { UseCategoriesFromUrlResult } from './HomePage.types';

/**
 * Хук для определения количества колонок в ленте промптов
 * в зависимости от размера экрана
 *
 * Соответствует логике CSS Grid из Feed.scss:
 * - mobile (до 374px): 1 колонка
 * - mobile (375px - 1023px): 2 колонки
 * - tablet (1024px - 1365px): 3 колонки
 * - desktop (1366px - 1919px): 4 колонки
 * - large-desktop (1440px - 1919px): 5 колонок
 * - extea-large-desktop (1920px+): 6 колонок
 *
 * @returns {PromptFeedColumns} Количество колонок для текущего размера экрана
 */
export const usePromptFeedColumns = () => {
    const screenSize = useGetScreenSize();

    switch (screenSize) {
        case ScreenSize.Mobile:
            return 2;
        case ScreenSize.Tablet:
            return 3;
        case ScreenSize.SmallDesktop:
            return 3;
        case ScreenSize.MiddleDesktop:
            return 4;
        case ScreenSize.Desktop:
            return 4;
        case ScreenSize.LargeDesktop:
            return 5;
        case ScreenSize.ExtraLargeDesktop:
            return 6;
        default:
            return 2; // fallback для неопределенных случаев
    }
};

const useSyncCategoryFromUrl = () => {
    const { setCategory } = useActions(feedActions);
    const { category: urlCategory } = useParams<{ category?: string }>();

    const categoryFromUrl = parseCategoryFromUrl(urlCategory);

    useEffect(() => {
        setCategory(categoryFromUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryFromUrl]);

    return categoryFromUrl;
};

export const useSelectedCategories = (): UseCategoriesFromUrlResult => {
    const { category } = useFeedSelector();
    const navigate = useNavigate();

    const updateSelectedCategories = useCallback(
        (categories: string[]) => {
            const newCategory = categories[0] || null;
            const newPath = buildCategoryPath(newCategory);

            navigate(newPath, { replace: true });
        },
        [navigate],
    );

    useSyncCategoryFromUrl();

    // Возвращаем массив для совместимости с существующим API компонентов
    const selectedCategories = useMemo(() => (category ? [category] : []), [category]);

    // Мемоизируем результат для избежания создания нового объекта при каждом рендере
    return useMemo(
        () => ({
            selectedCategories,
            updateSelectedCategories,
        }),
        [selectedCategories, updateSelectedCategories],
    );
};
